import React, { useState, useEffect } from "react";
import styled from "styled-components";
import colors from "styles/colors";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import facebookActions from "modules/facebook/actions";
import facebookSelectors from "modules/facebook/selectors";
import googleActions from "modules/google/actions";
import googleSelectors from "modules/google/selectors";
import TitleLabel from "components/TitleLabel";
import AccountBox from "components/AccountBox";
import TargetCard from "components/TargetCard";
import GoogleSigninButton from "components/facebook/GoogleSigninButton";
import googleHelper from "../../helper/google";

const GoogleSettingContent = ({ leadFormId }) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const { REACT_APP_GOOGLE_LEADS_API_APP_ID, REACT_APP_GOOGLE_API_DEVELOPER_KEY } = process.env;
  const rmpToken = sessionStorage.getItem("rmp_token");
  const lang = sessionStorage.getItem("lang") ? sessionStorage.getItem("lang") : "en";
  const googleAccountResult = useSelector((state) => googleSelectors.getGoogleAccountResult(state));
  const googleUserInfoResult = useSelector((state) => googleSelectors.getGoogleExchangeTokenResult(state));
  const googleSheetsListResult = useSelector((state) => googleSelectors.getGoogleSheetsListResult(state));
  const changeSheetsUrlInputResult = useSelector((state) => facebookSelectors.changeSheetsUrlInputResult(state));
  const googleSettingResult = useSelector((state) => facebookSelectors.getGoogleSettingResult(state));
  const googleConnectedResult = useSelector((state) => facebookSelectors.setGoogleConnectedResult(state));
  const googleDisconnectedResult = useSelector((state) => facebookSelectors.setGoogleDisconnectedResult(state));
  const validateGoogleSheetsUrlResult = useSelector((state) => googleSelectors.setValidateGoogleSheetsUrlResult(state));
  const googleLoading = useSelector((state) => googleSelectors.setGoogleLoadingResult(state));
  const googleSheetsLoading = useSelector((state) => googleSelectors.setGoogleSheetsLoadingResult(state));
  const googleSheetsError = useSelector((state) => googleSelectors.setGoogleSheetsErrorResult(state));
  const [isGoogleSearch, setIsGoogleSearch] = useState();
  const [googleData, setGoogleData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(
      facebookActions.getGoogleSetting({
        token: rmpToken,
        id: leadFormId,
      }),
    );
  }, []);

  useEffect(() => {
    const createData = (accountName, accountImageLink, spreadsheetUrl, isSearch) => ({
      accountName,
      accountImageLink,
      spreadsheetUrl,
      isSearch,
    });

    /**
     * don't have connection setting in database
     */
    const handleGoogleUserInfoResult = () => {
      if (googleUserInfoResult.result !== 0) return;
      if (googleSheetsError.data.error) return;
      const data = createData(
        googleUserInfoResult.data.googleAccount?.google_account,
        googleUserInfoResult.data.googleAccount?.google_account_image,
        "",
        false,
      );
      setGoogleData(data);
      dispatch(facebookActions.changeSheetsUrlInput(data.spreadsheetUrl));
      setIsGoogleSearch(data.isSearch);
    };

    /**
     * have connection setting in database
     */
    const handleGoogleSettingResult = () => {
      if (googleSettingResult.result !== 0) return;
      if (googleSettingResult.result === 0 && googleSettingResult.msg === "NO_DATA") return;
      if (!googleConnectedResult.data.googleConnected) return;
      if (googleSheetsError.data.error) return;
      const data = createData(
        googleSettingResult.data.google_account,
        googleSettingResult.data.google_account_image,
        googleSettingResult.data.spreadsheet_url,
        true,
      );
      setGoogleData(data);
      dispatch(facebookActions.changeSheetsUrlInput(data.spreadsheetUrl));
      setIsGoogleSearch(data.isSearch);
    };

    setGoogleData({});

    if (googleUserInfoResult.result === 0) {
      if (
        googleSettingResult.result === 0 &&
        googleSettingResult.msg === "GET_DATA_COMPLETED" &&
        !googleDisconnectedResult.data.googleDisconnected
      ) {
        if (googleSettingResult.data.google_message_error === "token_expire") {
          handleGoogleSettingResult();
        } else {
          handleGoogleUserInfoResult();
        }
      } else {
        handleGoogleUserInfoResult();
      }
    } else {
      if (!googleDisconnectedResult.data.googleDisconnected) {
        handleGoogleSettingResult();
      }
    }
  }, [googleSettingResult, googleUserInfoResult, googleConnectedResult, googleDisconnectedResult, googleSheetsError]);

  /**
   * get Google Sheets data by url
   * and set action after get data
   * if response is error, set error message
   * else set loading to false
   * @param {string} url
   */
  const getGoogleSheetsList = async (url) => {
    await dispatch(googleActions.setGoogleSheetsLoading(true));
    await dispatch(googleActions.setValidateGoogleSheetsUrl({ result: false }));
    const googleSheets = await dispatch(
      googleActions.getGoogleSheetsList({
        token: rmpToken,
        refreshToken:
          googleUserInfoResult.result === 0
            ? googleUserInfoResult.data.tokens?.refresh_token
            : googleAccountResult.data.refresh_token,
        googleAccount:
          googleUserInfoResult.result === 0
            ? googleUserInfoResult.data.googleAccount?.google_account
            : googleAccountResult.data.google_account,
        spreadsheetUrl: url,
      }),
    );

    if (url == "") {
      await dispatch(googleActions.setValidateGoogleSheetsUrl({ result: true, msg: "empty_url" }));
      setLoading(false);
      await dispatch(googleActions.setGoogleSheetsLoading(false));
    } else if (googleSheets.result === 1 && googleSheets.msg === "invalid_url") {
      await dispatch(googleActions.setValidateGoogleSheetsUrl({ result: true, msg: "invalid_url" }));
      setLoading(false);
      await dispatch(googleActions.setGoogleSheetsLoading(false));
    } else if (googleSheets.result === 1 && googleSheets.msg === "INVALID_PARAMS") {
      await dispatch(googleActions.setValidateGoogleSheetsUrl({ result: true, msg: "invalid_params" }));
      setLoading(false);
      await dispatch(googleActions.setGoogleSheetsLoading(false));
    } else if (googleSheets.result === 1 && googleSheets.msg === "invalid_mimetype") {
      await dispatch(googleActions.setValidateGoogleSheetsUrl({ result: true, msg: "invalid_mimetype" }));
      setLoading(false);
      await dispatch(googleActions.setGoogleSheetsLoading(false));
    } else if (googleSheets.result === 1) {
      await dispatch(googleActions.setValidateGoogleSheetsUrl({ result: true, msg: "error" }));
      setLoading(false);
      await dispatch(googleActions.setGoogleSheetsLoading(false));
    } else {
      setLoading(false);
      await dispatch(googleActions.setGoogleSheetsLoading(false));
    }
  };

  /**onchange Google Sheets url input */
  const handleGoogleSheetsUrlInput = (event) => {
    dispatch(facebookActions.changeSheetsUrlInput(event.target.value.trim()));
  };

  /** onBlur and onKeydown at google sheets url input*/
  const handleInput = (event) => {
    if (event.key === "Enter" || event.type === "blur") {
      setLoading(true);
      getGoogleSheetsList(event.target.value.trim());
      setIsGoogleSearch(true);
    }
  };

  /**
   * click browse button to open Google Picker
   */
  const handleBrowseButton = async () => {
    if (!sessionStorage.getItem("google_access_token") || sessionStorage.getItem("google_access_token") === "") {
      const initTokenResult = await googleHelper.initClientToken();

      // Set token to store
      const exchangeToken = await dispatch(
        googleActions.getGoogleExchangeToken({
          token: rmpToken,
          code: initTokenResult.code,
        }),
      );

      if (exchangeToken.result === 0) {
        sessionStorage.setItem("google_access_token", exchangeToken.data.tokens?.access_token);
      }
    }

    const view = new google.picker.DocsView(google.picker.ViewId.SPREADSHEETS);
    view.setMode(google.picker.DocsViewMode.LIST);

    const picker = new google.picker.PickerBuilder()
      .addView(view)
      .setOAuthToken(sessionStorage.getItem("google_access_token") ? sessionStorage.getItem("google_access_token") : "")
      .setDeveloperKey(REACT_APP_GOOGLE_API_DEVELOPER_KEY)
      .setCallback((data) => {
        if (data.action === "picked") {
          if (data.docs.length > 0) {
            let sheetsUrl = data.docs[0].url;
            dispatch(facebookActions.changeSheetsUrlInput(sheetsUrl));
            setLoading(true);
            getGoogleSheetsList(sheetsUrl);
            setIsGoogleSearch(true);
          }
        }
      })
      .setAppId(REACT_APP_GOOGLE_LEADS_API_APP_ID)
      .build();
    picker.setVisible(true);
  };

  const InputContainer = (errorStatus) => {
    return (
      <SheetsInputContainer>
        <SearchInput onClick={() => handleBrowseButton()} className="browse-button" errorStatus={errorStatus}>
          {t("__browse")}
        </SearchInput>
        <StyledInput
          type="text"
          className="input-box"
          borderColor={errorStatus ? colors.negative : colors.inputBorder}
          borderTopLeftRadius="0px"
          borderBottomLeftRadius="0px"
          borderTopRightRadius="6px"
          borderBottomRightRadius="6px"
          placeholder="https://docs.google.com/spreadsheets/d/..."
          value={changeSheetsUrlInputResult.data.sheetsUrl}
          onChange={handleGoogleSheetsUrlInput}
          onKeyDown={(event) => {
            handleInput(event);
          }}
          onBlur={(event) => {
            handleInput(event);
          }}
          disabled={loading || googleSheetsLoading.data.loading}
        />
      </SheetsInputContainer>
    );
  };

  const getFileData = (google) => {
    const file = {
      googleAccount: null,
      name: google.name,
      fileLink: google.webViewLink,
      folderType: google.type,
      folderName: null,
      folderCount: null,
      folderUrl: null,
      updatedDate: google.modifiedTime,
      updatedByImage: google.lastModifyingUser?.photoLink,
      updatedBy: google.lastModifyingUser?.emailAddress,
      position: "modal",
    };
    return file;
  };

  const File = () => {
    return (
      isGoogleSearch &&
      (!loading ? (
        googleSheetsListResult.result === 0 && !googleSheetsLoading.data.loading ? (
          <>
            <TargetCard
              type="googleSheets"
              data={getFileData(googleSheetsListResult.data)}
              leadFormId={leadFormId}
              leadType="facebook"
            />
          </>
        ) : googleSheetsLoading.data.loading ? (
          <LoadingArea position="file">
            <StyledLoadingIcon></StyledLoadingIcon>
          </LoadingArea>
        ) : null
      ) : (
        <LoadingArea position="file">
          <StyledLoadingIcon></StyledLoadingIcon>
        </LoadingArea>
      ))
    );
  };
  return Object.keys(googleData).length !== 0 && !googleLoading.data.loading ? (
    <>
      <TitleLabel title={t("__fbleads_google_account")} required={false} />
      <AccountBox
        imageLink={googleData.accountImageLink}
        name={googleData.accountName}
        accountType="google"
        leadType="facebook"
      />
      <TitleLabel title={t("__fbleads_google_sheets_url")} required={true} />
      {InputContainer(validateGoogleSheetsUrlResult.data.validateSheets.result)}
      {validateGoogleSheetsUrlResult.data.validateSheets.result &&
      validateGoogleSheetsUrlResult.data.validateSheets.msg !== "error" &&
      validateGoogleSheetsUrlResult.data.validateSheets.msg != "invalid_mimetype" ? (
        <TextWarning>{t("__sheets_input_warning")}</TextWarning>
      ) : validateGoogleSheetsUrlResult.data.validateSheets.result &&
        validateGoogleSheetsUrlResult.data.validateSheets.msg == "error" ? (
        <TextWarning>{t("__fbleads_google_error_search_file")}</TextWarning>
      ) : validateGoogleSheetsUrlResult.data.validateSheets.result &&
        validateGoogleSheetsUrlResult.data.validateSheets.msg == "invalid_mimetype" ? (
        <TextWarning>{t("__fbleads_google_error_search_file_type")}</TextWarning>
      ) : null}
      {File()}
    </>
  ) : googleSheetsListResult.result == 1 && !googleLoading.data.loading && googleSheetsError.data.error ? (
    <GoogleSigninButton googleStatus="error" />
  ) : googleLoading.data.loading ? (
    <LoadingArea position="googleArea">
      <StyledLoadingIcon />
    </LoadingArea>
  ) : (
    <GoogleSigninButton />
  );
};

export default GoogleSettingContent;

const StyledInput = styled.input.attrs((props) => ({
  padding: props.padding || "10px 16px",
  margin: props.margin || "0px",
  minWidth: props.minWidth,
  onChange: props.onChange,
  borderColor: props.borderColor,
  borderTopLeftRadius: props.borderTopLeftRadius !== undefined ? props.borderTopLeftRadius : "6px",
  borderBottomLeftRadius: props.borderBottomLeftRadius !== undefined ? props.borderBottomLeftRadius : "6px",
  borderTopRightRadius: props.borderTopRightRadius !== undefined ? props.borderTopRightRadius : "6px",
  borderBottomRightRadius: props.borderBottomRightRadius !== undefined ? props.borderBottomRightRadius : "6px",
}))`
  font-size: 14px;
  color: #555;
  padding: ${(props) => props.padding};
  margin: ${(props) => props.margin};
  border: 1px solid ${(props) => props.borderColor};
  height: 45px;
  border-top-left-radius: ${(props) => props.borderTopLeftRadius};
  border-bottom-left-radius: ${(props) => props.borderBottomLeftRadius};
  border-top-right-radius: ${(props) => props.borderTopRightRadius};
  border-bottom-right-radius: ${(props) => props.borderBottomRightRadius};
  width: 80%;
  border-left: hidden;

  &.-disabled {
    opacity: 0.5;
  }

  &.-icon {
    padding-left: 30px;
    box-sizing: border-box;
  }

  &:only-child {
    margin-bottom: 0;
  }

  ::placeholder {
    color: #aaa;
  }
`;

const TextWarning = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  color: ${colors.negative};
  font-size: 12px;
  font-weight: normal;
  position: relative;
  line-height: 1.5rem;
`;

const LoadingArea = styled.div.attrs((props) => ({
  position: props.position,
}))`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  width: 100%;
  height: ${(props) => (props.position == "file" ? "225px" : "100%")};
`;

const StyledLoadingIcon = styled.div`
  border: 5px solid #ccc;
  border-top: 5px solid #eee;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: button-keyframe-spin 1s linear infinite;
  box-sizing: initial !important;
  position: sticky;

  @keyframes button-keyframe-spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const SheetsInputContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 45px;
  width: 100%;
`;

const SearchInput = styled.div.attrs((props) => ({
  borderColor: props.errorStatus ? colors.negative : colors.inputBorder,
}))`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: #fff;
  color: #555;
  border: 1px solid ${(props) => props.borderColor};
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
  border-top-right-radius: 0px;
  border-bottom-right-radius: 0px;
  width: 20%;
  font-size: 14px;
  .browse-button {
    height: 45px;
    background-color: #fff;
    color: #555;
    border: 1px solid ${colors.inputBorder};
    border-top-left-radius: 0px;
    border-bottom-left-radius: 0px;
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }
`;
